import React, { useCallback, useState, useEffect } from "react";
import styled from "styled-components";
import Dial from "./Dial";
import {
  getAttack,
  getDecay,
  getSustain,
  getRelease,
  getAdsrEnabled,
  setAttack,
  setDecay,
  setSustain,
  setRelease,
  setAdsrEnabled,
} from "../utils/audioUtils";

const ADSRControls: React.FC = () => {
  // State for ADSR parameters
  const [attack, setAttackValue] = useState<number>(getAttack());
  const [decay, setDecayValue] = useState<number>(getDecay());
  const [sustain, setSustainValue] = useState<number>(getSustain());
  const [release, setReleaseValue] = useState<number>(getRelease());
  const [adsrEnabled, setAdsrEnabledValue] =
    useState<boolean>(getAdsrEnabled());

  // Handle attack change
  const handleAttackChange = useCallback((value: number) => {
    setAttackValue(value);
    setAttack(value);
    console.log(`Attack time set to ${value.toFixed(2)}s`);
  }, []);

  // Handle decay change
  const handleDecayChange = useCallback((value: number) => {
    setDecayValue(value);
    setDecay(value);
    console.log(`Decay time set to ${value.toFixed(2)}s`);
  }, []);

  // Handle sustain change
  const handleSustainChange = useCallback((value: number) => {
    setSustainValue(value);
    setSustain(value);
    console.log(`Sustain level set to ${(value * 100).toFixed(0)}%`);
  }, []);

  // Handle release change
  const handleReleaseChange = useCallback((value: number) => {
    setReleaseValue(value);
    setRelease(value);
    console.log(`Release time set to ${value.toFixed(2)}s`);
  }, []);

  const handleAdsrEnabledChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const enabled = event.target.checked;
      setAdsrEnabledValue(enabled);
      setAdsrEnabled(enabled);
    },
    [],
  );

  // Format percentage for display
  const formatPercentage = useCallback((value: number) => {
    return `${Math.round(value * 100)}%`;
  }, []);

  // Periodically check for ADSR value changes from external sources (e.g., waveform dragging)
  // Use a reasonable polling rate that doesn't interfere with dragging
  useEffect(() => {
    const interval = setInterval(() => {
      const currentAttack = getAttack();
      const currentDecay = getDecay();
      const currentSustain = getSustain();
      const currentRelease = getRelease();
      const currentAdsrEnabled = getAdsrEnabled();

      // Check if any values have changed externally
      if (Math.abs(currentAttack - attack) > 0.001) {
        setAttackValue(currentAttack);
      }
      if (Math.abs(currentDecay - decay) > 0.001) {
        setDecayValue(currentDecay);
      }
      if (Math.abs(currentSustain - sustain) > 0.001) {
        setSustainValue(currentSustain);
      }
      if (Math.abs(currentRelease - release) > 0.001) {
        setReleaseValue(currentRelease);
      }
      if (currentAdsrEnabled !== adsrEnabled) {
        setAdsrEnabledValue(currentAdsrEnabled);
      }
    }, 200); // Check every 200ms (reduced frequency to avoid interference)

    return () => clearInterval(interval);
  }, [attack, decay, sustain, release, adsrEnabled]);

  return (
    <Container id="adsr-controls">
      <Title id="adsr-controls-title">
        <span>Envelope</span>
        <ToggleInput
          id="adsr-envelope-toggle"
          type="checkbox"
          checked={adsrEnabled}
          onChange={handleAdsrEnabledChange}
          aria-label="Enable ADSR envelope"
        />
      </Title>
      <Grid id="adsr-controls-grid">
        <Dial
          value={attack}
          min={0.001}
          max={1.0}
          onChange={handleAttackChange}
          label="Attack"
          color="#4CAF50"
          valueFormatter={(val) => `${val.toFixed(2)}s`}
          variant="small"
          hideValue
        />
        <Dial
          value={decay}
          min={0.01}
          max={2.0}
          onChange={handleDecayChange}
          label="Decay"
          color="#FF9800"
          valueFormatter={(val) => `${val.toFixed(2)}s`}
          variant="small"
          hideValue
        />
        <Dial
          value={sustain}
          min={0}
          max={1.0}
          onChange={handleSustainChange}
          label="Sustain"
          color="#2196F3"
          valueFormatter={formatPercentage}
          variant="small"
          hideValue
        />
        <Dial
          value={release}
          min={0.01}
          max={3.0}
          onChange={handleReleaseChange}
          label="Release"
          color="#E91E63"
          valueFormatter={(val) => `${val.toFixed(2)}s`}
          variant="small"
          hideValue
        />
      </Grid>
    </Container>
  );
};

export default ADSRControls;

// Styled Components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  background: transparent;
  border: 2px solid #ccc;
  border-radius: 8px;
  padding: 20px 4px 4px 4px;
  min-width: 150px;
  max-width: 160px;
  position: relative;
  margin-top: 2px;
  box-sizing: border-box;

  /* Responsive design for smaller screens */
  @media (max-width: 768px) {
    min-width: 140px;
    max-width: 150px;
    padding: 18px 12px 12px 12px;
  }
`;

const Title = styled.h4`
  margin: 0;
  font-size: 14px;
  font-weight: bold;
  color: #333;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  text-align: center;
  letter-spacing: 0.5px;
  position: absolute;
  top: -8px;
  left: 50%;
  transform: translateX(-50%);
  background: #e2e1dd;
  padding: 0 12px;
  border-radius: 4px;

  @media (max-width: 768px) {
    font-size: 12px;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-template-rows: repeat(2, 1fr);
  justify-items: center;
  align-items: center;
  margin-top: 6px;

  @media (max-width: 768px) {
    gap: 6px 6px;
    margin-top: 6px;
  }
`;

const ToggleInput = styled.input`
  width: 14px;
  height: 14px;
  cursor: pointer;
  margin: 0;
`;
